import { dbDateFormat, parseSortableDate } from "../../classes/utils/DateUtils";
import DicomService from "../../services/DicomService";
import HeicService from "../../services/HeicService";
import NiftiService from "../../services/NiftiService";
import {
  DICOM_NAMING_PATTERNS,
  KNOWN_DOUBLE_EXTS,
  LIKELY_NON_EXTENSIONS,
  MEDICAL_EXTENSIONS,
} from "../constants/fileConstants";
import {
  FileCategories,
  MODALITY,
  standardizeModality,
} from "../enums/MedicalImagesEnum";
import { readImageMetadata } from "./ImageUtils";

const isSystemFile = fileName => {
  return (
    fileName.startsWith("._") ||
    fileName.startsWith(".DS_Store") ||
    fileName.startsWith("Thumbs.db") ||
    (fileName.startsWith(".") && fileName !== ".unknown")
  );
};

export function getFileExtension(file) {
  const name = file.name.toLowerCase().trim();

  // Handle empty or invalid names
  if (!name || name === "." || name === "..") {
    return ".unknown";
  }

  // Check for known double extensions first
  for (const ext of KNOWN_DOUBLE_EXTS) {
    if (name.endsWith(ext)) {
      return ext;
    }
  }

  const lastDotIndex = name.lastIndexOf(".");

  // No extension found
  if (lastDotIndex < 0 || lastDotIndex === name.length - 1) {
    return ".unknown";
  }

  const potentialExtension = name.substring(lastDotIndex);

  // Check if it's a known medical extension
  if (MEDICAL_EXTENSIONS.has(potentialExtension)) {
    return potentialExtension;
  }

  // Check if the "extension" is likely not a real extension
  for (const pattern of LIKELY_NON_EXTENSIONS) {
    if (pattern.test(potentialExtension)) {
      return ".unknown";
    }
  }

  // If extension is longer than 5 chars (excluding dot), likely not a real extension
  if (potentialExtension.length > 6) {
    return ".unknown";
  }

  // If extension contains only numbers, likely not a real extension
  if (/^\.\d+$/.test(potentialExtension)) {
    return ".unknown";
  }

  // Return the extension if it passes all checks
  return potentialExtension;
}

// Enhanced DICOM detection that also considers filename patterns
const detectFileTypeBySignature = async file => {
  return new Promise(resolve => {
    // First check filename patterns before reading file content
    const fileName = file.name;

    // Check against DICOM naming patterns
    for (const pattern of DICOM_NAMING_PATTERNS) {
      if (pattern.test(fileName)) {
        return resolve(FileCategories.DICOM);
      }
    }

    // If no pattern match, proceed with binary signature detection
    const reader = new FileReader();
    reader.onload = event => {
      const arrayBuffer = event.target.result;
      const uint8Array = new Uint8Array(arrayBuffer);

      // DICOM signature checks
      if (arrayBuffer.byteLength >= 132) {
        const dicomSignature = new Uint8Array(arrayBuffer.slice(128, 132));
        const dicomText = String.fromCharCode.apply(null, dicomSignature);
        if (dicomText === "DICM") {
          return resolve(FileCategories.DICOM);
        }
      }

      const bytes = uint8Array.subarray(0, 16);
      const hex = Array.from(bytes)
        .map(b => b.toString(16).padStart(2, "0"))
        .join("");

      // Image format signatures
      if (hex.startsWith("ffd8ff")) return resolve(FileCategories.IMAGE);
      if (hex.startsWith("89504e47")) return resolve(FileCategories.IMAGE);
      if (hex.startsWith("47494638")) return resolve(FileCategories.IMAGE);
      if (hex.startsWith("424d")) return resolve(FileCategories.IMAGE);
      if (hex.startsWith("49492a00") || hex.startsWith("4d4d002a"))
        return resolve(FileCategories.IMAGE);

      // NIfTI signature
      if (hex.indexOf("6e2b31") !== -1) return resolve(FileCategories.NIFTI);

      // HEIC signature
      if (
        hex.indexOf("66747970") !== -1 &&
        arrayBuffer.indexOf("heic") !== -1
      ) {
        return resolve(FileCategories.HEIC);
      }

      // Additional DICOM signature patterns
      if (
        hex.startsWith("0001") ||
        hex.indexOf("0800") !== -1 ||
        hex.indexOf("0200") !== -1
      ) {
        return resolve(FileCategories.DICOM);
      }

      return resolve(FileCategories.UNKNOWN);
    };

    reader.onerror = () => {
      resolve(FileCategories.UNKNOWN);
    };

    const blob = file.slice(0, 4096);
    reader.readAsArrayBuffer(blob);
  });
};

// Improved file type detection with better logic flow
const getFileType = async file => {
  const extension = getFileExtension(file);

  // If we have a known medical extension, trust it
  if (MEDICAL_EXTENSIONS.has(extension)) {
    const typeMap = {
      ".dcm": FileCategories.DICOM,
      ".dicom": FileCategories.DICOM,
      ".dic": FileCategories.DICOM,
      ".nii": FileCategories.NIFTI,
      ".nii.gz": FileCategories.NIFTI,
      ".mha": FileCategories.MHD,
      ".mhd": FileCategories.MHD,
      ".heic": FileCategories.HEIC,
      ".heif": FileCategories.HEIC,
    };

    if (typeMap[extension]) {
      return typeMap[extension];
    }

    // Default image extensions
    if (
      [".jpg", ".jpeg", ".png", ".tif", ".tiff", ".bmp", ".gif"].includes(
        extension
      )
    ) {
      return FileCategories.IMAGE;
    }
  }

  // Check MIME type for unknown extensions
  if (extension === ".unknown" && file.type) {
    const mimeToCategory = {
      "image/jpeg": FileCategories.IMAGE,
      "image/png": FileCategories.IMAGE,
      "image/gif": FileCategories.IMAGE,
      "image/bmp": FileCategories.IMAGE,
      "image/tiff": FileCategories.IMAGE,
      "application/dicom": FileCategories.DICOM,
      "image/heic": FileCategories.HEIC,
    };

    const categoryFromMime = mimeToCategory[file.type.toLowerCase()];
    if (categoryFromMime) {
      return categoryFromMime;
    }
  }

  // For unknown extensions or unrecognized MIME types, use signature detection
  if (extension === ".unknown" || !MEDICAL_EXTENSIONS.has(extension)) {
    const detectedType = await detectFileTypeBySignature(file);

    return detectedType;
  }

  return FileCategories.UNKNOWN;
};

const getFileCreationDate = file => {
  return new Promise(resolve => {
    const date = new Date(file.lastModified);
    if (!isNaN(date.getTime())) {
      resolve(dbDateFormat(date));
    } else {
      resolve(null);
    }
  });
};

export const processFiles = async (files, allowedFileTypes) => {
  const results = [];
  const processedFiles = new Set();

  const extendedAllowedTypes = allowedFileTypes.includes(".unknown")
    ? allowedFileTypes
    : [...allowedFileTypes, ".unknown"];

  for (const file of files) {
    if (processedFiles.has(file.name)) {
      continue;
    }

    processedFiles.add(file.name);
    const extension = getFileExtension(file);

    if (extendedAllowedTypes.includes(extension)) {
      const metadata = await readFileMetadata({ name: file.name, file });
      results.push(metadata);
      continue;
    }

    if (extension === ".unknown" || extension === ".") {
      const detectedType = await detectFileTypeBySignature(file);
      if (FileCategories[detectedType]) {
        const metadata = await readFileMetadata({
          name: file.name,
          file,
          detectedType: FileCategories[detectedType],
        });
        results.push(metadata);
      } else {
      }
    }
  }

  return results;
};

export const processFilesInBatches = async (
  files,
  allowedFileTypes,
  batchSize = 20,
  onProgress = () => {}
) => {
  const results = [];
  const batches = [];
  const processedFileNames = new Set();

  const filteredFiles = files.filter(file => !isSystemFile(file.name));

  const extendedAllowedTypes = allowedFileTypes.includes(".unknown")
    ? allowedFileTypes
    : [...allowedFileTypes, ".unknown"];

  for (let i = 0; i < filteredFiles.length; i += batchSize) {
    batches.push(filteredFiles.slice(i, i + batchSize));
  }

  for (let i = 0; i < batches.length; i++) {
    const filesWithKnownExtensions = [];
    const filesWithUnknownExtensions = [];

    for (let file of batches[i]) {
      // Use webkitRelativePath when available so that two files with the same
      // filename in different subdirectories are treated as distinct files.
      const fileKey = file.webkitRelativePath || file.name;
      if (processedFileNames.has(fileKey)) {
        continue;
      }

      let extension = getFileExtension(file);
      if (extension === ".unknown") {
        const detectedType = await detectFileTypeBySignature(file);
        if (detectedType && detectedType !== FileCategories.UNKNOWN) {
          const extMap = {
            [FileCategories.DICOM]: ".dcm",
            [FileCategories.NIFTI]: ".nii",
            [FileCategories.HEIC]: ".heic",
            [FileCategories.IMAGE]: ".jpg",
          };
          const newExt = extMap[detectedType];
          if (newExt) {
            file = new File([file], file.name + newExt, {
              type: file.type,
              lastModified: file.lastModified,
            });
            extension = newExt;
          }
          const metadata = await readFileMetadata({
            name: file.name,
            file,
            detectedType,
          });
          results.push(metadata);
        } else {
          results.push({ file, error: "unsupported-format" });
        }
      } else if (extendedAllowedTypes.includes(extension)) {
        filesWithKnownExtensions.push(file);
      } else {
        results.push({ file, name: file.name, error: "unsupported-format" });
      }
    }

    for (const file of filesWithKnownExtensions) {
      processedFileNames.add(file.webkitRelativePath || file.name);
      const fileType = await getFileType(file);
      const metadata = await readFileMetadata({
        name: file.name,
        file,
        detectedType: fileType,
      });
      results.push(metadata);
    }

    for (const file of filesWithUnknownExtensions) {
      processedFileNames.add(file.webkitRelativePath || file.name);
      const fileType = await detectFileTypeBySignature(file);

      if (FileCategories[fileType]) {
        const metadata = await readFileMetadata({
          name: file.name,
          file,
          detectedType: FileCategories[fileType],
        });
        results.push(metadata);
      } else {
      }
    }

    onProgress(((i + 1) / batches.length) * 100);
  }
  return results;
};

const readFileMetadata = async fileObj => {
  const { file, detectedType } = fileObj;
  let creationDate = await getFileCreationDate(file);
  let modality = null;
  let orientation = null;
  let seriesId = null;
  let studyId = null;
  let instanceId = null;
  let instanceNumber = null;
  let seriesNumber = null;

  let patientInfo = {
    firstName: null,
    lastName: null,
    birthDate: null,
    gender: null,
  };

  let additionalMetadata = {};

  const fileType = detectedType || (await getFileType(file));

  switch (fileType) {
    case FileCategories.DICOM:
      try {
        const dicomInfo = await DicomService.readMetadata(file);
        if (!dicomInfo) {
          return fileObj;
        }

        seriesId = dicomInfo.seriesId;
        studyId = dicomInfo.studyId;
        instanceId = dicomInfo.instanceId;

        instanceNumber = dicomInfo.instanceNumber;
        seriesNumber = dicomInfo.seriesNumber;

        creationDate = dicomInfo.creationDate || creationDate;
        modality = standardizeModality(dicomInfo.modality);
        orientation = dicomInfo.orientation;

        if (dicomInfo.patientInfo) {
          patientInfo = {
            ...patientInfo,
            ...dicomInfo.patientInfo,
          };
        }

        if (instanceNumber !== undefined) {
          additionalMetadata.instanceNumber = instanceNumber;
        }

        if (seriesNumber !== undefined) {
          additionalMetadata.seriesNumber = seriesNumber;
        }

        if (dicomInfo.imagePosition) {
          additionalMetadata.imagePosition = dicomInfo.imagePosition;
        }

        // New validation fields
        const dicomValidation = {
          imageType:          dicomInfo.imageType        ?? [],
          pixelDataPresent:   dicomInfo.pixelDataPresent ?? false,
          pixelSpacing:       dicomInfo.pixelSpacing      ?? null,
          imagerPixelSpacing: dicomInfo.imagerPixelSpacing ?? null,
          rescaleIntercept:   dicomInfo.rescaleIntercept  ?? undefined,
          rescaleSlope:       dicomInfo.rescaleSlope      ?? undefined,
        };
        additionalMetadata = { ...additionalMetadata, ...dicomValidation };
      } catch (error) {}
      break;

    case FileCategories.HEIC:
      try {
        const heicInfo = await HeicService.readMetadata(file);

        const jpegFile = await HeicService.convertToJpeg(file);

        fileObj.file = jpegFile;
        fileObj.name = jpegFile.name;
        fileObj.detectedType = FileCategories.IMAGE;

        creationDate = heicInfo.creationDate || creationDate;
        modality = standardizeModality(heicInfo.modality);
        seriesId = heicInfo.seriesId;

        if (heicInfo.patientInfo) {
          patientInfo = {
            ...patientInfo,
            ...heicInfo.patientInfo,
          };
        }

        additionalMetadata.convertedFromHeic = true;
        additionalMetadata.originalFileName = file.name;
      } catch (error) {}
      break;

    case FileCategories.NIFTI:
      try {
        const niftiInfo = await NiftiService.readMetadata(file);
        if (!niftiInfo) {
          return fileObj;
        }

        creationDate = niftiInfo.creationDate || creationDate;
        modality = standardizeModality(niftiInfo.modality);
        orientation = niftiInfo.orientation;
        seriesId = niftiInfo.seriesId;
        studyId = niftiInfo.studyId;
        instanceId = niftiInfo.instanceId;

        if (niftiInfo.patientInfo) {
          patientInfo = {
            ...patientInfo,
            ...niftiInfo.patientInfo,
          };
        }

        if (niftiInfo.dimensions)
          additionalMetadata.dimensions = niftiInfo.dimensions;
        if (niftiInfo.pixelDimensions)
          additionalMetadata.pixelDimensions = niftiInfo.pixelDimensions;
        if (niftiInfo.description)
          additionalMetadata.description = niftiInfo.description;
        if (niftiInfo.niftiVersion !== undefined)
          additionalMetadata.niftiVersion = niftiInfo.niftiVersion;
        if (niftiInfo.maxDimension !== undefined)
          additionalMetadata.maxDimension = niftiInfo.maxDimension;
      } catch (error) {}
      break;

    case FileCategories.MHD:
      try {
        modality = standardizeModality("CT");
      } catch (error) {}
      break;

    case FileCategories.IMAGE:
      try {
        const exifDate = await readImageMetadata(file);
        if (exifDate) {
          creationDate = exifDate;
        }
        modality = MODALITY.MO;
      } catch (error) {}
      break;

    default:
      modality = MODALITY.OT;
      break;
  }

  return {
    ...fileObj,
    creationDate,
    modality,
    orientation,
    imagePosition: additionalMetadata.imagePosition,
    seriesId,
    studyId,
    instanceId,
    patientInfo,
    additionalMetadata,
  };
};

export const updateFileMetadata = (file, field, value) => {
  if (field === "creationDate") {
    const parsedDate = parseSortableDate(value);
    if (parsedDate && !isNaN(parsedDate.getTime())) {
      value = dbDateFormat(parsedDate);
    } else {
      value = null;
    }
  } else if (field === "modality") {
    value = standardizeModality(value);
  }
  return { ...file, [field]: value };
};

const extractZPosition = file => {
  if (file.imagePosition) {
    const positions = file.imagePosition.split("\\").map(v => parseFloat(v));
    if (positions.length === 3) {
      return positions[2];
    }
  }
  return null;
};

const calculateSliceThickness = files => {
  const zPositions = files
    .map(file => extractZPosition(file))
    .filter(z => z !== null)
    .sort((a, b) => a - b);

  if (zPositions.length < 2) return 5.0;

  const gaps = [];
  for (let i = 1; i < zPositions.length; i++) {
    const gap = Math.abs(zPositions[i] - zPositions[i - 1]);
    if (gap > 0.1) {
      gaps.push(gap);
    }
  }

  if (gaps.length === 0) return 5.0;

  gaps.sort((a, b) => a - b);
  const median = gaps[Math.floor(gaps.length / 2)];
  return median;
};

const detectSpatialGroups = files => {
  const firstFile = files[0];
  if (
    !firstFile ||
    (firstFile.modality !== MODALITY.CT && firstFile.modality !== MODALITY.MR)
  ) {
    return [files];
  }

  const filesWithPosition = files.filter(
    file => extractZPosition(file) !== null
  );
  const filesWithoutPosition = files.filter(
    file => extractZPosition(file) === null
  );

  if (filesWithPosition.length === 0) {
    return [files];
  }

  const sortedFiles = [...filesWithPosition].sort((a, b) => {
    return extractZPosition(a) - extractZPosition(b);
  });

  const expectedThickness = calculateSliceThickness(sortedFiles);

  const minGapThreshold = Math.max(expectedThickness * 2, 10);
  const maxGap = minGapThreshold;

  const allZPositions = sortedFiles.map(f => extractZPosition(f));
  let largestGap = 0;
  let largestGapIndex = -1;

  for (let i = 1; i < allZPositions.length; i++) {
    const gap = Math.abs(allZPositions[i] - allZPositions[i - 1]);
    if (gap > largestGap) {
      largestGap = gap;
      largestGapIndex = i;
    }
  }

  const largeGaps = [];
  for (let i = 1; i < allZPositions.length; i++) {
    const gap = Math.abs(allZPositions[i] - allZPositions[i - 1]);
    if (gap > expectedThickness * 1.5) {
      largeGaps.push({
        index: i,
        gap: gap,
        fromZ: allZPositions[i - 1],
        toZ: allZPositions[i],
      });
    }
  }

  if (largeGaps.length > 0) {
    largeGaps.forEach((item, idx) => {
      if (idx < 5) {
      }
    });
  }

  const spatialGroups = [];
  let currentGroup = [sortedFiles[0]];

  for (let i = 1; i < sortedFiles.length; i++) {
    const currentZ = extractZPosition(sortedFiles[i]);
    const prevZ = extractZPosition(sortedFiles[i - 1]);
    const gap = Math.abs(currentZ - prevZ);

    if (gap > maxGap) {
      spatialGroups.push(currentGroup);
      currentGroup = [sortedFiles[i]];
    } else {
      currentGroup.push(sortedFiles[i]);
    }
  }
  spatialGroups.push(currentGroup);

  if (filesWithoutPosition.length > 0) {
    spatialGroups[0] = [...spatialGroups[0], ...filesWithoutPosition];
  }

  spatialGroups.forEach((group, index) => {
    const zPositions = group
      .map(f => extractZPosition(f))
      .filter(z => z !== null);
    const minZ = zPositions.length > 0 ? Math.min(...zPositions) : "N/A";
    const maxZ = zPositions.length > 0 ? Math.max(...zPositions) : "N/A";
  });

  return spatialGroups;
};

const extractOrientation = file => {
  if (file.orientation) {
    if (file.orientation.startsWith("[[") && file.orientation.endsWith("]]")) {
      try {
        const parsed = JSON.parse(file.orientation);
        if (
          Array.isArray(parsed) &&
          parsed.length === 2 &&
          Array.isArray(parsed[0]) &&
          Array.isArray(parsed[1]) &&
          parsed[0].length === 3 &&
          parsed[1].length === 3
        ) {
          return {
            rowDirection: parsed[0],
            colDirection: parsed[1],
          };
        }
      } catch (e) {}
    }

    if (file.orientation.includes("\\")) {
      const values = file.orientation.split("\\").map(v => parseFloat(v));
      if (values.length === 6) {
        return {
          rowDirection: [values[0], values[1], values[2]],
          colDirection: [values[3], values[4], values[5]],
        };
      }
    }
  } else {
  }
  return null;
};

const orientationToString = orientation => {
  if (!orientation) return "unknown";
  const { rowDirection, colDirection } = orientation;
  return `[${rowDirection.join(",")}][${colDirection.join(",")}]`;
};

const orientationsMatch = (orientation1, orientation2) => {
  if (!orientation1 || !orientation2) return false;

  const tolerance = 0.001;

  for (let i = 0; i < 3; i++) {
    if (
      Math.abs(orientation1.rowDirection[i] - orientation2.rowDirection[i]) >
      tolerance
    ) {
      return false;
    }
  }

  for (let i = 0; i < 3; i++) {
    if (
      Math.abs(orientation1.colDirection[i] - orientation2.colDirection[i]) >
      tolerance
    ) {
      return false;
    }
  }

  return true;
};

const findDominantOrientation = files => {
  const orientationCounts = new Map();
  const orientationObjects = new Map();

  files.forEach(file => {
    const orientation = extractOrientation(file);
    if (orientation) {
      const orientationStr = orientationToString(orientation);
      orientationCounts.set(
        orientationStr,
        (orientationCounts.get(orientationStr) || 0) + 1
      );
      orientationObjects.set(orientationStr, orientation);
    }
  });

  if (orientationCounts.size === 0) return null;

  let maxCount = 0;
  let dominantOrientationStr = null;

  for (const [orientationStr, count] of orientationCounts.entries()) {
    if (count > maxCount) {
      maxCount = count;
      dominantOrientationStr = orientationStr;
    }
  }

  return {
    orientation: orientationObjects.get(dominantOrientationStr),
    orientationStr: dominantOrientationStr,
    count: maxCount,
    allOrientations: Array.from(orientationCounts.entries()),
  };
};

const filterByConsistentOrientation = (group, problematicGroups) => {
  const { files } = group;

  const shouldFilter =
    group.modality === MODALITY.CT || group.modality === MODALITY.MR;
  if (!shouldFilter) {
    return group;
  }

  const dominantInfo = findDominantOrientation(files);

  if (!dominantInfo) {
    return group;
  }

  const {
    orientation: dominantOrientation,
    orientationStr,
    count,
    allOrientations,
  } = dominantInfo;

  if (allOrientations.length > 1) {
    allOrientations.forEach(([orientStr, fileCount]) => {});
  }

  const consistentFiles = [];
  const inconsistentFiles = [];

  files.forEach(file => {
    const fileOrientation = extractOrientation(file);

    if (!fileOrientation) {
      consistentFiles.push(file);
      return;
    }

    if (orientationsMatch(fileOrientation, dominantOrientation)) {
      consistentFiles.push(file);
    } else {
      inconsistentFiles.push(file);
      const fileOrientationStr = orientationToString(fileOrientation);
    }
  });

  if (inconsistentFiles.length > 0) {
    problematicGroups.push({
      originalGroupId: group.id,
      dominantOrientation: orientationStr,
      filteredFiles: inconsistentFiles.map(file => ({
        name: file.name,
        orientation: orientationToString(extractOrientation(file)),
      })),
      reason: "orientation_mismatch",
    });
  }

  return {
    ...group,
    files: consistentFiles,
  };
};

export const groupFilesById = (files, filterOrientation = true) => {
  const groups = {};
  const problematicGroups = [];

  files.forEach((file, index) => {
    if (index < 3) {
    }

    const groupId = computeGroupId(file, index);

    if (!groups[groupId]) {
      groups[groupId] = {
        id: groupId,
        files: [],
        patientInfo: {
          firstName: null,
          lastName: null,
          birthDate: null,
          gender: null,
        },
        modality: null,
        orientation: null,
        creationDate: null,
        detectedType: null,
      };
    }

    groups[groupId].files.push(file);

    if (file.patientInfo) {
      if (
        file.patientInfo.firstName &&
        !groups[groupId].patientInfo.firstName
      ) {
        groups[groupId].patientInfo.firstName = file.patientInfo.firstName;
      }
      if (file.patientInfo.lastName && !groups[groupId].patientInfo.lastName) {
        groups[groupId].patientInfo.lastName = file.patientInfo.lastName;
      }
      if (
        file.patientInfo.birthDate &&
        !groups[groupId].patientInfo.birthDate
      ) {
        groups[groupId].patientInfo.birthDate = file.patientInfo.birthDate;
      }
      if (file.patientInfo.gender && !groups[groupId].patientInfo.gender) {
        groups[groupId].patientInfo.gender = file.patientInfo.gender;
      }
    }

    if (file.modality && !groups[groupId].modality) {
      groups[groupId].modality = file.modality;
    }
    if (file.orientation && !groups[groupId].orientation) {
      groups[groupId].orientation = file.orientation;
    }
    if (file.creationDate && !groups[groupId].creationDate) {
      groups[groupId].creationDate = file.creationDate;
    }
    if (file.detectedType && !groups[groupId].detectedType) {
      groups[groupId].detectedType = file.detectedType;
    }
  });

  Object.values(groups).forEach(group => {
    group.files.sort((a, b) => {
      const aInstanceNumber = getInstanceNumber(a);
      const bInstanceNumber = getInstanceNumber(b);

      if (aInstanceNumber !== null && bInstanceNumber !== null) {
        return aInstanceNumber - bInstanceNumber;
      }

      const aFileName = a.name.split(".")[0];
      const bFileName = b.name.split(".")[0];

      const aMatch = aFileName.match(/(\d+)$/);
      const bMatch = bFileName.match(/(\d+)$/);

      if (aMatch && bMatch) {
        return parseInt(aMatch[1], 10) - parseInt(bMatch[1], 10);
      }

      return a.name.localeCompare(b.name);
    });
  });

  Object.keys(groups).forEach(groupId => {});

  const originalGroupCount = Object.keys(groups).length;
  let spatialGroupsCreated = 0;

  const newGroups = {};

  Object.keys(groups).forEach(groupId => {
    const group = groups[groupId];
    const spatialGroups = detectSpatialGroups(group.files);

    if (spatialGroups.length === 1) {
      newGroups[groupId] = group;
    } else {
      spatialGroups.forEach((spatialGroup, index) => {
        const newGroupId =
          index === 0 ? groupId : `${groupId}_spatial_${index + 1}`;

        newGroups[newGroupId] = {
          ...group,
          id: newGroupId,
          files: spatialGroup,
        };

        if (index > 0) {
          spatialGroupsCreated++;
        }
      });
    }
  });

  Object.keys(groups).forEach(key => delete groups[key]);
  Object.assign(groups, newGroups);

  if (filterOrientation) {
    Object.keys(groups).forEach(groupId => {
      const originalGroup = groups[groupId];
      const filteredGroup = filterByConsistentOrientation(
        originalGroup,
        problematicGroups
      );
      groups[groupId] = filteredGroup;
    });

    if (problematicGroups.length > 0) {
      problematicGroups.forEach(problematicGroup => {
        problematicGroup.filteredFiles.forEach(file => {});
      });
    } else {
    }
  }

  const groupValues = Object.values(groups);
  const sortedGroups = groupValues.sort((a, b) => {
    if (a.creationDate && b.creationDate) {
      return new Date(b.creationDate) - new Date(a.creationDate);
    }

    if (a.creationDate) return -1;
    if (b.creationDate) return 1;

    return 0;
  });

  return { groups: sortedGroups, problematicGroups };
};

export const computeGroupId = (file, index = 0) => {
  // Special handling for X-ray modalities - one instance per group
  if (
    file.modality === MODALITY.DX ||
    file.modality === MODALITY.CR
  ) {
    // Use instance UID for X-ray modalities to ensure one file per group
    return (
      file.instanceId ||
      `${file.seriesId || file.studyId || file.name}_${
        file.instanceNumber || index
      }`
    );
  } else {
    // Original logic for other modalities (CT, MR, etc.)
    return (
      file.seriesId || file.studyId || `${file.name}_${Date.now()}_${index}`
    );
  }
};

function getInstanceNumber(file) {
  if (
    file.additionalMetadata &&
    file.additionalMetadata.instanceNumber !== undefined
  ) {
    return parseInt(file.additionalMetadata.instanceNumber, 10);
  }

  return null;
}
